import { Plus, Trash2 } from "lucide-react";
import { StationaryItemsTable } from "./stationary-items-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AccessState, SecondaryAccessRow } from "@/lib/plan-editor";

/**
 * Phase 25 — Plan Editor Access & Discounts section.
 *
 * Dumb controlled component. Ports the Access tab from the retired
 * popup editor (deleted in Plan 25-06).
 *
 * Renders:
 *  - Variable-length secondaryAccess rows: resourceType Select + discountPct
 *    Input + Remove button per row
 *  - "+ Add Access" button to append a new row
 *  - 2 registration switches: requiresBusinessRegistration, requiresGstRegistration
 *
 * Covers: ACCORD25-01 field surface (Access & Discounts).
 */

const RESOURCE_TYPES = [
  "Workstation",
  "Private_Cabin",
  "Meeting_Room",
  "Conference_Room",
] as const;
const RESOURCE_TYPE_LABELS: Record<string, string> = {
  Workstation: "Workstation",
  Private_Cabin: "Private Cabin",
  Meeting_Room: "Meeting Room",
  Conference_Room: "Conference Room",
};

export interface AccessSectionProps {
  value: AccessState;
  onChange: (next: AccessState) => void;
  /** Plan 25-04 — Save & Continue button click handler. */
  onSave: () => void | Promise<void>;
  /** Plan 25-04 — true while any section save is in flight. */
  saving: boolean;
  /** 0064 — plan id (NULL in create mode); needed by StationaryItemsTable */
  planId: number | null;
  /** 0064 — space id (NULL in create mode); needed by StationaryItemsTable */
  spaceId: number | null;
  /** 2026-06-09 — locks all inputs when true */
  disabled?: boolean;
  /** 2026-06-09 — Edit button handler */
  onEdit?: () => void;
}

export function AccessSection({
  value,
  onChange,
  onSave,
  saving,
  planId,
  spaceId,
  disabled = false,
  onEdit,
}: AccessSectionProps) {
  function addRow() {
    const next: SecondaryAccessRow = {
      resourceType: "Meeting_Room",
      discountPct: "",
    };
    onChange({ ...value, secondaryAccess: [...value.secondaryAccess, next] });
  }

  function removeRow(idx: number) {
    onChange({
      ...value,
      secondaryAccess: value.secondaryAccess.filter((_, i) => i !== idx),
    });
  }

  function updateRow(
    idx: number,
    field: keyof SecondaryAccessRow,
    nextValue: string,
  ) {
    onChange({
      ...value,
      secondaryAccess: value.secondaryAccess.map((row, i) =>
        i === idx ? { ...row, [field]: nextValue } : row,
      ),
    });
  }

  return (
    <div className="space-y-4 p-1" data-testid="plan-editor-access-section">
    <fieldset disabled={disabled} className="space-y-4 disabled:opacity-70">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Secondary Access (Cross-Plan)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={addRow}
            data-testid="add-secondary-access"
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Access
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Members on this plan can book other resource types at a discount (or
          free).
        </p>

        {value.secondaryAccess.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Resource Type</TableHead>
                  <TableHead>Discount %</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {value.secondaryAccess.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Select
                        value={row.resourceType || "none"}
                        onValueChange={(v) =>
                          updateRow(idx, "resourceType", v === "none" ? "" : v)
                        }
                      >
                        <SelectTrigger className="h-7 text-xs w-44">
                          <SelectValue placeholder="Pick type…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {RESOURCE_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {RESOURCE_TYPE_LABELS[t]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={row.discountPct}
                        onChange={(e) =>
                          updateRow(idx, "discountPct", e.target.value)
                        }
                        placeholder="0–100"
                        className="h-7 w-20 text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => removeRow(idx)}
                        title="Remove"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground rounded-md border bg-muted/30 p-3">
            No secondary access rows. Members see only their plan's primary
            resource type.
          </p>
        )}
      </div>

      <div className="space-y-3 border-t pt-4">
        <Label className="text-sm font-medium">Registration Requirements</Label>

        <div className="flex items-center gap-3">
          <Switch
            id="requiresBusinessRegistration"
            checked={value.requiresBusinessRegistration}
            onCheckedChange={(v) =>
              onChange({ ...value, requiresBusinessRegistration: v })
            }
          />
          <Label
            htmlFor="requiresBusinessRegistration"
            className="cursor-pointer text-sm"
          >
            Requires business registration
          </Label>
        </div>
        <p className="text-[10px] text-muted-foreground pl-11">
          Shows a warning in Convert Lead if the account is not B2B.
        </p>

        <div className="flex items-center gap-3">
          <Switch
            id="requiresGstRegistration"
            checked={value.requiresGstRegistration}
            onCheckedChange={(v) =>
              onChange({ ...value, requiresGstRegistration: v })
            }
          />
          <Label
            htmlFor="requiresGstRegistration"
            className="cursor-pointer text-sm"
          >
            Requires GST registration
          </Label>
        </div>
        <p className="text-[10px] text-muted-foreground pl-11">
          Shows a warning in Convert Lead if the account has no GSTIN.
        </p>
      </div>

      <div className="space-y-4 border-t pt-4">
        <div className="space-y-1">
          <Label className="text-sm font-medium">Included Services</Label>
          <p className="text-[10px] text-muted-foreground">
            Toggle a service on, then set the monthly quota (blank = unlimited).
          </p>
        </div>

        {/* Business / GST registration — boolean only (no quota).
            Distinct from the requires_* warnings above: these mean the
            plan BUNDLES registration assistance as a service to the
            member, not that the lead needs to already have it. */}
        {/* Business Registration — toggle gates cost + period */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Switch
              id="businessRegistrationIncluded"
              checked={value.businessRegistrationIncluded}
              onCheckedChange={(v) =>
                onChange({
                  ...value,
                  businessRegistrationIncluded: v,
                  businessRegistrationCost: v ? value.businessRegistrationCost : "",
                })
              }
            />
            <Label
              htmlFor="businessRegistrationIncluded"
              className="cursor-pointer text-sm"
            >
              Business Registration
            </Label>
          </div>
          {value.businessRegistrationIncluded && (
            <div className="pl-11">
              <div className="space-y-1.5 rounded-md border p-3 max-w-3xl">
                <Label>Cost</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={value.businessRegistrationCost}
                    onChange={(e) => onChange({ ...value, businessRegistrationCost: e.target.value })}
                    placeholder="e.g. 5000"
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">₹ per</span>
                  <Select
                    value={value.businessRegistrationCostPeriod || "Annual"}
                    onValueChange={(v) => onChange({ ...value, businessRegistrationCostPeriod: v })}
                  >
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Day">Day</SelectItem>
                      <SelectItem value="Month">Month</SelectItem>
                      <SelectItem value="Annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* GST Registration — toggle gates cost + period */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Switch
              id="gstRegistrationIncluded"
              checked={value.gstRegistrationIncluded}
              onCheckedChange={(v) =>
                onChange({
                  ...value,
                  gstRegistrationIncluded: v,
                  gstRegistrationCost: v ? value.gstRegistrationCost : "",
                })
              }
            />
            <Label
              htmlFor="gstRegistrationIncluded"
              className="cursor-pointer text-sm"
            >
              GST Registration
            </Label>
          </div>
          {value.gstRegistrationIncluded && (
            <div className="pl-11">
              <div className="space-y-1.5 rounded-md border p-3 max-w-3xl">
                <Label>Cost</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={value.gstRegistrationCost}
                    onChange={(e) => onChange({ ...value, gstRegistrationCost: e.target.value })}
                    placeholder="e.g. 3000"
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">₹ per</span>
                  <Select
                    value={value.gstRegistrationCostPeriod || "Annual"}
                    onValueChange={(v) => onChange({ ...value, gstRegistrationCostPeriod: v })}
                  >
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Day">Day</SelectItem>
                      <SelectItem value="Month">Month</SelectItem>
                      <SelectItem value="Annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mailing Service — boolean only (no quota) */}
        <div className="flex items-center gap-3">
          <Switch
            id="mailingServiceAllowed"
            checked={value.mailingServiceAllowed}
            onCheckedChange={(v) =>
              onChange({ ...value, mailingServiceAllowed: v })
            }
          />
          <Label
            htmlFor="mailingServiceAllowed"
            className="cursor-pointer text-sm"
          >
            Mailing Service Allowed
          </Label>
        </div>

        {/* Conference Room — toggle gates dynamic (value + unit + period) quota */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Switch
              id="conferenceRoomAccessAllowed"
              checked={value.conferenceRoomAccessAllowed}
              onCheckedChange={(v) =>
                onChange({
                  ...value,
                  conferenceRoomAccessAllowed: v,
                  conferenceRoomQuotaValue: v ? value.conferenceRoomQuotaValue : "",
                })
              }
            />
            <Label htmlFor="conferenceRoomAccessAllowed" className="cursor-pointer text-sm">
              Conference Room Access
            </Label>
          </div>
          {value.conferenceRoomAccessAllowed && (
            <div className="pl-11">
              <div className="space-y-1.5 rounded-md border p-3 max-w-3xl">
                <Label>Complimentary Quota</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    type="number"
                    min={0}
                    value={value.conferenceRoomQuotaValue}
                    onChange={(e) => onChange({ ...value, conferenceRoomQuotaValue: e.target.value })}
                    placeholder="e.g. 5"
                    className="w-24"
                  />
                  <Select
                    value={value.conferenceRoomQuotaUnit || "Hours"}
                    onValueChange={(v) => onChange({ ...value, conferenceRoomQuotaUnit: v })}
                  >
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Minutes">Minutes</SelectItem>
                      <SelectItem value="Hours">Hours</SelectItem>
                      <SelectItem value="Days">Days</SelectItem>
                      <SelectItem value="Months">Months</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">per</span>
                  <Select
                    value={value.conferenceRoomQuotaPeriod || "Month"}
                    onValueChange={(v) => onChange({ ...value, conferenceRoomQuotaPeriod: v })}
                  >
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Day">Day</SelectItem>
                      <SelectItem value="Month">Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Meeting Room — toggle gates dynamic (value + unit + period) quota */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Switch
              id="meetingRoomAccessAllowed"
              checked={value.meetingRoomAccessAllowed}
              onCheckedChange={(v) =>
                onChange({
                  ...value,
                  meetingRoomAccessAllowed: v,
                  meetingRoomQuotaValue: v ? value.meetingRoomQuotaValue : "",
                })
              }
            />
            <Label htmlFor="meetingRoomAccessAllowed" className="cursor-pointer text-sm">
              Meeting Room Access
            </Label>
          </div>
          {value.meetingRoomAccessAllowed && (
            <div className="pl-11">
              <div className="space-y-1.5 rounded-md border p-3 max-w-3xl">
                <Label>Complimentary Quota</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    type="number"
                    min={0}
                    value={value.meetingRoomQuotaValue}
                    onChange={(e) => onChange({ ...value, meetingRoomQuotaValue: e.target.value })}
                    placeholder="e.g. 3"
                    className="w-24"
                  />
                  <Select
                    value={value.meetingRoomQuotaUnit || "Hours"}
                    onValueChange={(v) => onChange({ ...value, meetingRoomQuotaUnit: v })}
                  >
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Minutes">Minutes</SelectItem>
                      <SelectItem value="Hours">Hours</SelectItem>
                      <SelectItem value="Days">Days</SelectItem>
                      <SelectItem value="Months">Months</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">per</span>
                  <Select
                    value={value.meetingRoomQuotaPeriod || "Month"}
                    onValueChange={(v) => onChange({ ...value, meetingRoomQuotaPeriod: v })}
                  >
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Day">Day</SelectItem>
                      <SelectItem value="Month">Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Print — toggle gates Black + Colour category inputs */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Switch
              id="complimentaryPrintAccess"
              checked={value.complimentaryPrintAccess}
              onCheckedChange={(v) =>
                onChange({
                  ...value,
                  complimentaryPrintAccess: v,
                  blackPrintPages: v ? value.blackPrintPages : "",
                  blackPrintOverageRate: v ? value.blackPrintOverageRate : "",
                  colorPrintPages: v ? value.colorPrintPages : "",
                  colorPrintOverageRate: v ? value.colorPrintOverageRate : "",
                })
              }
            />
            <Label htmlFor="complimentaryPrintAccess" className="cursor-pointer text-sm">
              Complimentary Print Access
            </Label>
          </div>
          {value.complimentaryPrintAccess && (
            <div className="space-y-3 pl-11 max-w-3xl">
              {/* Black / White — sub-toggle gates the inputs */}
              <div className="space-y-2 rounded-md border p-3">
                <div className="flex items-center gap-3">
                  <Switch
                    id="blackPrintEnabled"
                    checked={value.blackPrintEnabled}
                    onCheckedChange={(v) =>
                      onChange({
                        ...value,
                        blackPrintEnabled: v,
                        blackPrintPages: v ? value.blackPrintPages : "",
                        blackPrintOverageRate: v ? value.blackPrintOverageRate : "",
                      })
                    }
                  />
                  <Label htmlFor="blackPrintEnabled" className="cursor-pointer text-xs font-medium">
                    Black / White
                  </Label>
                </div>
                {value.blackPrintEnabled && (
                  <div className="grid grid-cols-2 gap-3 pl-11">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Complimentary Pages / month</Label>
                      <Input
                        type="number"
                        min={0}
                        value={value.blackPrintPages}
                        onChange={(e) => onChange({ ...value, blackPrintPages: e.target.value })}
                        placeholder="e.g. 100"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Overage Price / print (₹)</Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={value.blackPrintOverageRate}
                        onChange={(e) => onChange({ ...value, blackPrintOverageRate: e.target.value })}
                        placeholder="e.g. 1"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Colour — sub-toggle gates the inputs */}
              <div className="space-y-2 rounded-md border p-3">
                <div className="flex items-center gap-3">
                  <Switch
                    id="colorPrintEnabled"
                    checked={value.colorPrintEnabled}
                    onCheckedChange={(v) =>
                      onChange({
                        ...value,
                        colorPrintEnabled: v,
                        colorPrintPages: v ? value.colorPrintPages : "",
                        colorPrintOverageRate: v ? value.colorPrintOverageRate : "",
                      })
                    }
                  />
                  <Label htmlFor="colorPrintEnabled" className="cursor-pointer text-xs font-medium">
                    Colour
                  </Label>
                </div>
                {value.colorPrintEnabled && (
                  <div className="grid grid-cols-2 gap-3 pl-11">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Complimentary Pages / month</Label>
                      <Input
                        type="number"
                        min={0}
                        value={value.colorPrintPages}
                        onChange={(e) => onChange({ ...value, colorPrintPages: e.target.value })}
                        placeholder="e.g. 20"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Overage Price / print (₹)</Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={value.colorPrintOverageRate}
                        onChange={(e) => onChange({ ...value, colorPrintOverageRate: e.target.value })}
                        placeholder="e.g. 5"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Stationary — toggle gates per-item line-table backed by plan_entitlements */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Switch
              id="complimentaryStationary"
              checked={value.complimentaryStationary}
              onCheckedChange={(v) =>
                onChange({ ...value, complimentaryStationary: v })
              }
            />
            <Label htmlFor="complimentaryStationary" className="cursor-pointer text-sm">
              Complimentary Stationary
            </Label>
          </div>
          {value.complimentaryStationary && (
            <div className="pl-11">
              {planId !== null && spaceId !== null ? (
                <StationaryItemsTable planId={planId} spaceId={spaceId} />
              ) : (
                <p className="text-xs text-muted-foreground rounded-md border bg-muted/30 p-3">
                  Save the plan first (Basics → Save & Continue) — then add
                  per-item stationery quotas here.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      </fieldset>
      <div className="flex justify-end pt-2">
        {disabled ? (
          <Button
            type="button"
            variant="outline"
            onClick={onEdit}
            data-testid="plan-editor-access-edit"
          >
            Edit
          </Button>
        ) : (
          <Button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="plan-editor-access-save"
        >
          {saving ? "Saving…" : "Save & Continue"}
        </Button>
        )}
      </div>
    </div>
  );
}
